import { useCallback, useMemo } from 'react';
import { usePortalContext } from '../contexts/PortalContext';
import { useTheme } from './useTheme';

interface BrandingColors {
  primaryColor: string;
  secondaryColor: string;
  logoUrl?: string;
}

interface BrandingUtilities extends BrandingColors {
  getPrimaryWithOpacity: (opacity: number) => string;
  getSecondaryWithOpacity: (opacity: number) => string;
  getContrastText: (color: string) => string;
  getThemedPrimaryColor: () => string;
  getThemedSecondaryColor: () => string;
  getThemedPrimaryWithOpacity: (opacity: number) => string;
  getThemedSecondaryWithOpacity: (opacity: number) => string;
}

// Pure utility functions moved outside component to prevent recreating on each render
const hexToRgb = (hex: string) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
};

export function usePortalBranding(): BrandingUtilities {
  const { organizationInfo } = usePortalContext();
  const { actualTheme } = useTheme();

  const branding = organizationInfo?.portalSettings?.branding;

  const primaryColor = branding?.primaryColor || '#3B82F6';
  const secondaryColor = branding?.secondaryColor || '#6B7280';
  const logoUrl = branding?.logoUrl;

  // Memoize theme-adjusted colors to prevent recalculation
  const themedPrimaryColor = useMemo(() => {
    if (actualTheme !== 'dark') return primaryColor;

    // Adjust color for dark mode to improve contrast
    const rgb = hexToRgb(primaryColor);
    if (!rgb) return primaryColor;

    const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;

    // If color is too dark in dark mode, lighten it
    if (luminance < 0.4) {
      const factor = 1.3;
      const newR = Math.min(255, Math.round(rgb.r * factor));
      const newG = Math.min(255, Math.round(rgb.g * factor));
      const newB = Math.min(255, Math.round(rgb.b * factor));
      const toHex = (value: number) => value.toString(16).padStart(2, '0');
      return `#${toHex(newR)}${toHex(newG)}${toHex(newB)}`;
    }

    // If luminance is between 0.4 and 0.6, slightly brighten
    if (luminance < 0.6) {
      const factor = 1.15;
      const newR = Math.min(255, Math.round(rgb.r * factor));
      const newG = Math.min(255, Math.round(rgb.g * factor));
      const newB = Math.min(255, Math.round(rgb.b * factor));
      const toHex = (value: number) => value.toString(16).padStart(2, '0');
      return `#${toHex(newR)}${toHex(newG)}${toHex(newB)}`;
    }

    return primaryColor;
  }, [actualTheme, primaryColor]);

  const themedSecondaryColor = useMemo(() => {
    if (actualTheme !== 'dark') return secondaryColor;

    // Adjust color for dark mode to improve contrast
    const rgb = hexToRgb(secondaryColor);
    if (!rgb) return secondaryColor;

    const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;

    // If color is too dark in dark mode, lighten it
    if (luminance < 0.4) {
      const factor = 1.3;
      const newR = Math.min(255, Math.round(rgb.r * factor));
      const newG = Math.min(255, Math.round(rgb.g * factor));
      const newB = Math.min(255, Math.round(rgb.b * factor));
      const toHex = (value: number) => value.toString(16).padStart(2, '0');
      return `#${toHex(newR)}${toHex(newG)}${toHex(newB)}`;
    }

    // If luminance is between 0.4 and 0.6, slightly brighten
    if (luminance < 0.6) {
      const factor = 1.15;
      const newR = Math.min(255, Math.round(rgb.r * factor));
      const newG = Math.min(255, Math.round(rgb.g * factor));
      const newB = Math.min(255, Math.round(rgb.b * factor));
      const toHex = (value: number) => value.toString(16).padStart(2, '0');
      return `#${toHex(newR)}${toHex(newG)}${toHex(newB)}`;
    }

    return secondaryColor;
  }, [actualTheme, secondaryColor]);

  // Memoize all returned functions to prevent unnecessary re-renders
  const getPrimaryWithOpacity = useCallback((opacity: number): string => {
    const rgb = hexToRgb(primaryColor);
    if (!rgb) return primaryColor;
    return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${opacity})`;
  }, [primaryColor]);

  const getSecondaryWithOpacity = useCallback((opacity: number): string => {
    const rgb = hexToRgb(secondaryColor);
    if (!rgb) return secondaryColor;
    return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${opacity})`;
  }, [secondaryColor]);

  const getContrastText = useCallback((backgroundColor: string): string => {
    const rgb = hexToRgb(backgroundColor);
    if (!rgb) return '#ffffff';

    // Calculate luminance
    const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
    return luminance > 0.5 ? '#000000' : '#ffffff';
  }, []);

  const getThemedPrimaryColor = useCallback((): string => {
    return themedPrimaryColor;
  }, [themedPrimaryColor]);

  const getThemedSecondaryColor = useCallback((): string => {
    return themedSecondaryColor;
  }, [themedSecondaryColor]);

  const getThemedPrimaryWithOpacity = useCallback((opacity: number): string => {
    const rgb = hexToRgb(themedPrimaryColor);
    if (!rgb) return themedPrimaryColor;

    // In dark mode, slightly increase opacity for better visibility
    const adjustedOpacity = actualTheme === 'dark' ? Math.min(1, opacity * 1.1) : opacity;
    return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${adjustedOpacity})`;
  }, [themedPrimaryColor, actualTheme]);

  const getThemedSecondaryWithOpacity = useCallback((opacity: number): string => {
    const rgb = hexToRgb(themedSecondaryColor);
    if (!rgb) return themedSecondaryColor;

    // In dark mode, slightly increase opacity for better visibility
    const adjustedOpacity = actualTheme === 'dark' ? Math.min(1, opacity * 1.1) : opacity;
    return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${adjustedOpacity})`;
  }, [themedSecondaryColor, actualTheme]);

  return {
    primaryColor,
    secondaryColor,
    logoUrl,
    getPrimaryWithOpacity,
    getSecondaryWithOpacity,
    getContrastText,
    getThemedPrimaryColor,
    getThemedSecondaryColor,
    getThemedPrimaryWithOpacity,
    getThemedSecondaryWithOpacity,
  };
}